import { useState } from 'react';
import { Lock, Power } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@easyflow/ui-master/components/ui/card';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { Badge } from '@easyflow/ui-master/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@easyflow/ui-master/components/ui/table';
import { TerminateBackendDialog } from './TerminateBackendDialog';
import type { LongRunningTransaction, TerminateBackendRequest } from '@easyflow/shared';

interface Props {
  transactions: LongRunningTransaction[];
  onTerminated: () => void;
}

function durationLabel(seconds: number): string {
  if (seconds < 60) return `${seconds.toFixed(0)}s`;
  if (seconds < 3600) return `${(seconds / 60).toFixed(1)}m`;
  return `${(seconds / 3600).toFixed(1)}h`;
}

export function LongRunningTxCard({ transactions, onTerminated }: Props) {
  const [target, setTarget] = useState<{ pid: number; mode: TerminateBackendRequest['mode'] } | null>(null);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Lock className="h-5 w-5 text-rose-500" />
          Long-running transactions
          <Badge variant="outline" className="ml-1 text-xs">{transactions.length}</Badge>
        </CardTitle>
        <CardDescription>Transazioni aperte da &gt; 60s — rischio bloat WAL e lock</CardDescription>
      </CardHeader>
      <CardContent>
        {transactions.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">Nessuna transazione lunga aperta</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>PID</TableHead>
                <TableHead>App</TableHead>
                <TableHead>User</TableHead>
                <TableHead>State</TableHead>
                <TableHead className="text-right">Durata tx</TableHead>
                <TableHead>Blocked by</TableHead>
                <TableHead>Query</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {transactions.map((t) => (
                <TableRow key={t.pid}>
                  <TableCell className="font-mono text-xs">{t.pid}</TableCell>
                  <TableCell className="text-xs">{t.application_name ?? '—'}</TableCell>
                  <TableCell className="text-xs">{t.usename ?? '—'}</TableCell>
                  <TableCell className="text-xs">{t.state ?? '—'}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-rose-600">
                    {durationLabel(t.xact_duration_seconds)}
                  </TableCell>
                  <TableCell className="text-xs font-mono">
                    {t.blocked_by.length === 0 ? '—' : t.blocked_by.join(', ')}
                  </TableCell>
                  <TableCell className="text-xs font-mono max-w-md truncate" title={t.query}>{t.query}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs gap-1"
                      onClick={() => setTarget({ pid: t.pid, mode: 'terminate' })}
                    >
                      <Power className="h-3 w-3" />
                      Terminate
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <TerminateBackendDialog
        target={target}
        onClose={() => setTarget(null)}
        onSuccess={() => {
          setTarget(null);
          onTerminated();
        }}
      />
    </Card>
  );
}
